import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../Context/ThemeContext';
import { useDashboardData, useGameHistory, useTopPerformers, useTopPerformersByMode, useActiveUsers } from './hooks';
import { calculatePercentageChange } from './utils/mappers';
import { exportDashboardPdf } from './utils/exportPdf';
import { exportDashboardCsv, type DashboardExportData } from './utils/exportCsv';
import { defaultPeriodState, type PeriodOption, type ChartDataPoint, type PeriodState, type CustomDateRange, type CardKey } from './types';
import {
  UniversalFilter,
  StatCard,
  DailyChallengeCard,
  ChartCard,
  ChartKey,
  RevenueCard,
  PerformersCard,
  BottomStatCard,
  TopPerformersModal,
  ActiveUsersModal,
} from './Components';

// Helpers
const getPeriodLabel = (period: PeriodOption): string => {
  switch (period) {
    case '1D': return 'from yesterday';
    case '7D': return 'from last week';
    case '30D': return 'from last month';
    case '90D': return 'from last quarter';
    case 'custom': return 'vs comparison period';
  }
};

const formatComparisonStat = (comparison: { currentPeriodTotal: number; percentageChange: number } | undefined, label: string, periodLabel: string) => {
  if (!comparison) return null;
  return {
    label,
    value: comparison.currentPeriodTotal.toLocaleString(),
    change: `${comparison.percentageChange >= 0 ? '+' : ''}${comparison.percentageChange.toFixed(2)}% ${periodLabel}`,
    positive: comparison.percentageChange >= 0,
  };
};

const Dashboard: React.FC = () => {
  const { theme, isMobile, isTablet } = useTheme();
  const navigate = useNavigate();

  // Consolidated period state (includes customRanges)
  const [periods, setPeriods] = useState<PeriodState>(defaultPeriodState);

  // Top 10 modal state
  const [modalMode, setModalMode] = useState<{ mode: string; modeKey: string } | null>(null);
  const { performers: modalPerformers, loading: modalLoading, error: modalError, fetchTopPerformers, reset: resetModal } = useTopPerformersByMode();

  // Active users modal state
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);
  const { users: activeUsers, loading: activeUsersLoading, error: activeUsersError, totalCount: activeUsersTotalCount, page: activeUsersPage, pageSize: activeUsersPageSize, fetchActiveUsers, reset: resetActiveUsers } = useActiveUsers();

  const getModeKey = (mode: string): string => {
    switch (mode) {
      case 'VS Mode': return 'vs';
      case 'VS Bot': return 'bot';
      case 'Single Mode': return 'single';
      case 'Daily Challenge': return 'daily';
      default: return 'vs';
    }
  };

  // Chart line visibility
  const [activeLines, setActiveLines] = useState<Record<ChartKey, boolean>>({
    daily: true,
    vsRandom: true,
    vsFriends: true,
    vsBot: true,
    single: true,
  });

  // Custom range handlers
  const handleCustomRangeChange = useCallback((cardKey: CardKey, range: CustomDateRange | undefined) => {
    setPeriods((prev) => {
      if (range) {
        return {
          ...prev,
          [cardKey]: 'custom' as PeriodOption,
          customRanges: { ...prev.customRanges, [cardKey]: range },
        };
      } else {
        // Clear custom range - revert to default
        const { [cardKey]: _, ...restRanges } = prev.customRanges;
        return {
          ...prev,
          [cardKey]: defaultPeriodState[cardKey as keyof typeof defaultPeriodState] as PeriodOption,
          customRanges: restRanges,
        };
      }
    });
  }, []);

  const handleUniversalCustomRange = useCallback((range: CustomDateRange) => {
    setPeriods((prev) => ({
      ...prev,
      dailyActiveUsers: 'custom',
      vsGames: 'custom',
      singleGames: 'custom',
      dailyChallenge: 'custom',
      completedVsGames: 'custom',
      completedVsBots: 'custom',
      customRanges: {
        ...prev.customRanges,
        dailyActiveUsers: range,
        vsGames: range,
        singleGames: range,
        dailyChallenge: range,
        completedVsGames: range,
        completedVsBots: range,
      },
    }));
  }, []);

  // Active users modal handlers
  const handleViewActiveUsers = useCallback(() => {
    setShowActiveUsersModal(true);
    const period = periods.dailyActiveUsers;
    const customRange = period === 'custom' ? periods.customRanges.dailyActiveUsers : undefined;
    fetchActiveUsers(period, 1, customRange);
  }, [periods.dailyActiveUsers, periods.customRanges.dailyActiveUsers, fetchActiveUsers]);

  const handleActiveUsersPageChange = useCallback((page: number) => {
    const period = periods.dailyActiveUsers;
    const customRange = period === 'custom' ? periods.customRanges.dailyActiveUsers : undefined;
    fetchActiveUsers(period, page, customRange);
  }, [periods.dailyActiveUsers, periods.customRanges.dailyActiveUsers, fetchActiveUsers]);

  // Custom hooks for data fetching — pass custom ranges when in custom mode
  const dauCustomRange = periods.dailyActiveUsers === 'custom' ? periods.customRanges.dailyActiveUsers : undefined;
  const { statsData, loading: statsLoading, error: statsError, retry } = useDashboardData(dauCustomRange);

  const chartCustomRange = periods.chart === 'custom' ? periods.customRanges.chart : undefined;
  const { data: chartData, totalGames: totalGamesPlayed, loading: chartLoading } = useGameHistory(periods.chart, chartCustomRange);

  const topPerformersCustomRange = periods.topPerformers === 'custom' ? periods.customRanges.topPerformers : undefined;
  const { performers: topPerformers, loading: topPerformersLoading } = useTopPerformers(periods.topPerformers, topPerformersCustomRange);

  // Custom range hooks for cards that use custom date ranges
  const vsGamesCustomRange = periods.vsGames === 'custom' ? periods.customRanges.vsGames : undefined;
  const {
    vsGamesComparison: vsGamesCustomComparison,
    singleGamesComparison: singleGamesCustomComparison,
    dailyChallengeComparison: dailyChallengeCustomComparison,
    vsBotComparison: vsBotCustomComparison,
    vsGamesCompletedComparison: vsGamesCompletedCustomComparison,
    data: customData,
  } = useGameHistory(
    periods.vsGames === 'custom' ? 'custom' : '__noop__',
    vsGamesCustomRange,
  );

  // Historical data for stat calculations (cached by hooks)
  const {
    data: dailyData,
    vsGamesComparison: vsGames7DComparison,
    singleGamesComparison: singleGames7DComparison,
    vsGamesCompletedComparison: vsGamesCompleted7DComparison,
    dailyChallengeComparison: dailyChallenge7DComparison,
    vsBotComparison: vsBot7DComparison,
  } = useGameHistory('7D');
  const {
    data: weeklyData,
    vsGamesComparison: vsGames30DComparison,
    singleGamesComparison: singleGames30DComparison,
    vsGamesCompletedComparison: vsGamesCompleted30DComparison,
    dailyChallengeComparison: dailyChallenge30DComparison,
    vsBotComparison: vsBot30DComparison,
  } = useGameHistory('30D');
  const {
    data: monthlyData,
    vsGamesComparison: vsGames90DComparison,
    singleGamesComparison: singleGames90DComparison,
    vsGamesCompletedComparison: vsGamesCompleted90DComparison,
    dailyChallengeComparison: dailyChallenge90DComparison,
    vsBotComparison: vsBot90DComparison,
  } = useGameHistory('90D');

  // Period update helpers
  const updatePeriod = <K extends keyof PeriodState>(key: K, value: PeriodState[K]) => {
    setPeriods((prev) => {
      // When switching away from custom, clear the custom range
      if (value !== 'custom' && prev.customRanges[key as CardKey]) {
        const { [key as CardKey]: _, ...restRanges } = prev.customRanges;
        return { ...prev, [key]: value, customRanges: restRanges };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleUniversalFilterChange = (period: PeriodOption) => {
    setPeriods((prev) => {
      // Clear custom ranges for affected cards
      const { dailyActiveUsers: _1, vsGames: _2, singleGames: _3, dailyChallenge: _4, completedVsGames: _5, completedVsBots: _6, ...restRanges } = prev.customRanges;
      return {
        ...prev,
        dailyActiveUsers: period,
        vsGames: period,
        singleGames: period,
        dailyChallenge: period,
        completedVsGames: period,
        completedVsBots: period,
        customRanges: restRanges,
      };
    });
  };

  // Computed stats
  const getPeriodStats = useMemo(() => {
    return (period: PeriodOption) => {
      // Custom periods don't use local computation — they use API comparison data
      if (period === 'custom') {
        return {
          current: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          previous: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          changes: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          periodData: [],
        };
      }

      let data: ChartDataPoint[] = [];
      let currentIndex = -1;
      let previousIndex = -1;

      switch (period) {
        case '1D':
          data = dailyData;
          currentIndex = data.length - 1;
          previousIndex = data.length - 2;
          break;
        case '7D':
          data = dailyData;
          currentIndex = data.length - 1;
          previousIndex = 0;
          break;
        case '30D':
          data = weeklyData;
          currentIndex = data.length - 1;
          previousIndex = data.length - 2;
          break;
        case '90D':
          data = monthlyData;
          currentIndex = data.length - 1;
          previousIndex = data.length - 2;
          break;
      }

      if (data.length === 0 || currentIndex < 0) {
        return {
          current: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          previous: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          changes: { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsTotal: 0, total: 0 },
          periodData: [],
        };
      }

      if (period === '7D') {
        const weekSummed = data.reduce(
          (acc, d) => ({
            daily: acc.daily + d.daily,
            vsRandom: acc.vsRandom + d.vsRandom,
            vsFriends: acc.vsFriends + d.vsFriends,
            vsBot: acc.vsBot + d.vsBot,
            single: acc.single + d.single,
            vsGamesStarted: acc.vsGamesStarted + (d.vsGamesStarted || 0),
          }),
          { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsGamesStarted: 0 }
        );

        const weekVsTotal = weekSummed.vsGamesStarted + weekSummed.vsBot;
        const weekTotal = weekVsTotal + weekSummed.single + weekSummed.daily;
        const firstDay = data[0] || { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsGamesStarted: 0 };
        const lastDay = data[data.length - 1] || firstDay;

        return {
          current: { ...weekSummed, vsTotal: weekVsTotal, total: weekTotal },
          previous: { ...firstDay, vsTotal: (firstDay.vsGamesStarted || 0) + firstDay.vsBot, total: 0 },
          changes: {
            daily: calculatePercentageChange(firstDay.daily, lastDay.daily),
            vsRandom: calculatePercentageChange(firstDay.vsRandom, lastDay.vsRandom),
            vsFriends: calculatePercentageChange(firstDay.vsFriends, lastDay.vsFriends),
            vsBot: calculatePercentageChange(firstDay.vsBot, lastDay.vsBot),
            single: calculatePercentageChange(firstDay.single, lastDay.single),
            vsTotal: calculatePercentageChange(
              (firstDay.vsGamesStarted || 0) + firstDay.vsBot,
              (lastDay.vsGamesStarted || 0) + lastDay.vsBot
            ),
            total: 0,
          },
          periodData: data,
        };
      }

      const currentData = data[currentIndex];
      const previousData = previousIndex >= 0 ? data[previousIndex] : { daily: 0, vsRandom: 0, vsFriends: 0, vsBot: 0, single: 0, vsGamesStarted: 0 };

      const currentVsTotal = (currentData.vsGamesStarted || 0) + currentData.vsBot;
      const previousVsTotal = (previousData.vsGamesStarted || 0) + previousData.vsBot;
      const currentTotal = currentVsTotal + currentData.single + currentData.daily;
      const previousTotal = previousVsTotal + previousData.single + previousData.daily;

      return {
        current: { ...currentData, vsTotal: currentVsTotal, total: currentTotal },
        previous: { ...previousData, vsTotal: previousVsTotal, total: previousTotal },
        changes: {
          daily: calculatePercentageChange(previousData.daily, currentData.daily),
          vsRandom: calculatePercentageChange(previousData.vsRandom, currentData.vsRandom),
          vsFriends: calculatePercentageChange(previousData.vsFriends, currentData.vsFriends),
          vsBot: calculatePercentageChange(previousData.vsBot, currentData.vsBot),
          single: calculatePercentageChange(previousData.single, currentData.single),
          vsTotal: calculatePercentageChange(previousVsTotal, currentVsTotal),
          total: calculatePercentageChange(previousTotal, currentTotal),
        },
        periodData: data,
      };
    };
  }, [dailyData, weeklyData, monthlyData]);

  // Stat calculations
  const dailyActiveUsersStat = useMemo(() => {
    const period = periods.dailyActiveUsers;
    const label = 'Daily Active Users';

    if (!statsData) return { label, value: '---', change: 'Loading...', positive: true };

    // For custom ranges, use the custom stats response
    if (period === 'custom') {
      // When custom, the stats API returns data in the daily/weekly slots based on custom range
      const customStats = (statsData as any)?.customUsers;
      if (customStats) {
        return {
          label,
          value: (customStats.current || 0).toLocaleString(),
          change: `${(customStats.percentageChange || 0) >= 0 ? '+' : ''}${(customStats.percentageChange || 0).toFixed(2)}% ${getPeriodLabel(period)}`,
          positive: (customStats.percentageChange || 0) >= 0,
        };
      }
      // Fallback: use dailyUsers data (backend will populate this for custom ranges)
      const users = statsData.dailyUsers;
      if (users) {
        return {
          label,
          value: (users.current || 0).toLocaleString(),
          change: `${(users.percentageChange || 0) >= 0 ? '+' : ''}${(users.percentageChange || 0).toFixed(2)}% ${getPeriodLabel(period)}`,
          positive: (users.percentageChange || 0) >= 0,
        };
      }
      return { label, value: '---', change: 'Loading...', positive: true };
    }

    let usersValue = 0;
    let usersChange = 0;
    let hasData = true;

    if (period === '1D') {
      usersValue = statsData.dailyUsers?.current || 0;
      usersChange = statsData.dailyUsers?.percentageChange || 0;
    } else if (period === '7D') {
      usersValue = statsData.weeklyUsers?.current || 0;
      usersChange = statsData.weeklyUsers?.percentageChange || 0;
    } else if (period === '30D') {
      usersValue = statsData.monthlyUsers?.current || 0;
      usersChange = statsData.monthlyUsers?.percentageChange || 0;
    } else {
      const quarterlyValue = statsData.quarterlyUsers?.current || 0;
      const quarterlyChange = statsData.quarterlyUsers?.percentageChange;

      if (quarterlyValue === 0 || quarterlyChange === undefined) {
        hasData = false;
      } else {
        usersValue = quarterlyValue;
        usersChange = quarterlyChange;
      }
    }

    if (!hasData) {
      return { label, value: 'N/A', change: 'Insufficient data', positive: true };
    }

    return {
      label,
      value: usersValue.toLocaleString(),
      change: `${usersChange >= 0 ? '+' : ''}${usersChange.toFixed(2)}% ${getPeriodLabel(period)}`,
      positive: usersChange >= 0,
    };
  }, [statsData, periods.dailyActiveUsers]);

  const vsGamesStat = useMemo(() => {
    const period = periods.vsGames;
    const periodLbl = getPeriodLabel(period);

    // Custom range — use comparison data from custom hook
    if (period === 'custom' && vsGamesCustomComparison) {
      return formatComparisonStat(vsGamesCustomComparison, 'VS Games', periodLbl)!;
    }

    // Use API comparison data for accurate period-over-period comparisons
    if (period === '7D' && vsGames7DComparison) {
      return formatComparisonStat(vsGames7DComparison, 'VS Games', periodLbl)!;
    }
    if (period === '30D' && vsGames30DComparison) {
      return formatComparisonStat(vsGames30DComparison, 'VS Games', periodLbl)!;
    }
    if (period === '90D' && vsGames90DComparison) {
      return formatComparisonStat(vsGames90DComparison, 'VS Games', periodLbl)!;
    }

    // Fallback for 1D
    const periodStats = getPeriodStats(period);
    let vsTotal = 0;
    if (period === '1D') {
      vsTotal = periodStats.current.vsTotal;
    } else {
      for (const d of periodStats.periodData) {
        vsTotal += (d.vsGamesStarted || 0) + d.vsBot;
      }
    }
    return {
      label: 'VS Games',
      value: vsTotal.toLocaleString(),
      change: `${periodStats.changes.vsTotal >= 0 ? '+' : ''}${periodStats.changes.vsTotal.toFixed(2)}% ${periodLbl}`,
      positive: periodStats.changes.vsTotal >= 0,
    };
  }, [periods.vsGames, getPeriodStats, vsGames7DComparison, vsGames30DComparison, vsGames90DComparison, vsGamesCustomComparison]);

  const singleGamesStat = useMemo(() => {
    const period = periods.singleGames;
    const periodLbl = getPeriodLabel(period);

    if (period === 'custom' && singleGamesCustomComparison) {
      return formatComparisonStat(singleGamesCustomComparison, 'Single Games', periodLbl)!;
    }

    if (period === '7D' && singleGames7DComparison) {
      return formatComparisonStat(singleGames7DComparison, 'Single Games', periodLbl)!;
    }
    if (period === '30D' && singleGames30DComparison) {
      return formatComparisonStat(singleGames30DComparison, 'Single Games', periodLbl)!;
    }
    if (period === '90D' && singleGames90DComparison) {
      return formatComparisonStat(singleGames90DComparison, 'Single Games', periodLbl)!;
    }

    const periodStats = getPeriodStats(period);
    let singleTotal = 0;
    if (period === '1D') {
      singleTotal = periodStats.current.single;
    } else {
      for (const d of periodStats.periodData) {
        singleTotal += d.single;
      }
    }
    return {
      label: 'Single Games',
      value: singleTotal.toLocaleString(),
      change: `${periodStats.changes.single >= 0 ? '+' : ''}${periodStats.changes.single.toFixed(2)}% ${periodLbl}`,
      positive: periodStats.changes.single >= 0,
    };
  }, [periods.singleGames, getPeriodStats, singleGames7DComparison, singleGames30DComparison, singleGames90DComparison, singleGamesCustomComparison]);

  const dailyChallengeStatsData = useMemo(() => {
    if (!statsData?.dailyChallenge && periods.dailyChallenge !== 'custom') {
      return [
        { label: 'Completed:', value: '---', change: '---', positive: true },
        { label: 'Failed:', value: '---', change: '---', positive: false },
        { label: 'Success Rate:', value: '---', change: '---', positive: true },
      ];
    }

    const period = periods.dailyChallenge;
    const periodStats = getPeriodStats(period);
    let total = 0;
    let completed = 0;
    let failed = 0;

    const comparisonData = period === 'custom' ? dailyChallengeCustomComparison
      : period === '7D' ? dailyChallenge7DComparison
        : period === '30D' ? dailyChallenge30DComparison
          : period === '90D' ? dailyChallenge90DComparison
            : undefined;

    if (period === '1D') {
      const challenge = statsData!.dailyChallenge;
      completed = challenge.completed || 0;
      failed = challenge.failed || 0;
      total = completed + failed;
    } else if (comparisonData) {
      total = comparisonData.currentPeriodTotal;
      for (const d of (period === 'custom' ? customData : periodStats.periodData)) {
        completed += d.dailyCompleted || 0;
        failed += d.dailyFailed || 0;
      }
    } else {
      for (const d of periodStats.periodData) {
        total += d.daily;
        completed += d.dailyCompleted || 0;
        failed += d.dailyFailed || 0;
      }
    }

    const dailyChange = comparisonData?.percentageChange ?? periodStats.changes.daily;
    const successRate = total > 0 ? (completed / total) * 100 : 0;
    const failedPercent = total > 0 ? (failed / total * 100) : 0;
    const completedIsNoChange = dailyChange === 0;

    return [
      { label: 'Completed:', value: completed.toString(), change: `${dailyChange >= 0 ? '+' : ''}${dailyChange.toFixed(2)}%`, positive: dailyChange >= 0, neutral: completedIsNoChange },
      { label: 'Failed:', value: failed.toString(), change: `${failedPercent.toFixed(1)}%`, positive: false, neutral: true },
      { label: 'Success Rate:', value: `${successRate.toFixed(1)}%`, change: successRate >= 50 ? 'Good' : 'Needs improvement', positive: successRate >= 50, neutral: true },
    ];
  }, [statsData, periods.dailyChallenge, getPeriodStats, dailyChallenge7DComparison, dailyChallenge30DComparison, dailyChallenge90DComparison, dailyChallengeCustomComparison, customData]);

  const dailyChallengeMainStat = useMemo(() => {
    if (!statsData?.dailyChallenge && periods.dailyChallenge !== 'custom') {
      return { value: '---', change: 'Loading...', changePositive: true, successRate: '---' };
    }

    const period = periods.dailyChallenge;
    const periodStats = getPeriodStats(period);
    const periodLbl = getPeriodLabel(period);

    const comparisonData = period === 'custom' ? dailyChallengeCustomComparison
      : period === '7D' ? dailyChallenge7DComparison
        : period === '30D' ? dailyChallenge30DComparison
          : period === '90D' ? dailyChallenge90DComparison
            : undefined;

    let total = 0;
    let completed = 0;

    if (period === '1D') {
      const challenge = statsData!.dailyChallenge;
      completed = challenge.completed || 0;
      total = completed + (challenge.failed || 0);
    } else if (comparisonData) {
      total = comparisonData.currentPeriodTotal;
      for (const d of (period === 'custom' ? customData : periodStats.periodData)) {
        completed += d.dailyCompleted || 0;
      }
    } else {
      for (const d of periodStats.periodData) {
        total += d.daily;
        completed += d.dailyCompleted || 0;
      }
    }

    const dailyChange = comparisonData?.percentageChange ?? periodStats.changes.daily;
    const successRate = total > 0 ? (completed / total) * 100 : 0;

    return {
      value: total.toString(),
      change: `${dailyChange >= 0 ? '+' : ''}${dailyChange.toFixed(2)}% ${periodLbl}`,
      changePositive: dailyChange >= 0,
      successRate: `${successRate.toFixed(0)}%`,
    };
  }, [statsData, periods.dailyChallenge, getPeriodStats, dailyChallenge7DComparison, dailyChallenge30DComparison, dailyChallenge90DComparison, dailyChallengeCustomComparison, customData]);

  // Bottom stats
  const completedVsGamesStat = useMemo(() => {
    const period = periods.completedVsGames;
    const periodStats = getPeriodStats(period);
    const periodLbl = getPeriodLabel(period);

    const getBars = () => (period === 'custom' ? customData : periodStats.periodData).map((d) => d.vsGamesCompleted || 0);

    if (period === 'custom' && vsGamesCompletedCustomComparison) {
      return {
        ...formatComparisonStat(vsGamesCompletedCustomComparison, 'Completed VS Games', periodLbl)!,
        bars: getBars(),
      };
    }

    if (period === '7D' && vsGamesCompleted7DComparison) {
      return {
        ...formatComparisonStat(vsGamesCompleted7DComparison, 'Completed VS Games', periodLbl)!,
        bars: getBars(),
      };
    }
    if (period === '30D' && vsGamesCompleted30DComparison) {
      return {
        ...formatComparisonStat(vsGamesCompleted30DComparison, 'Completed VS Games', periodLbl)!,
        bars: getBars(),
      };
    }
    if (period === '90D' && vsGamesCompleted90DComparison) {
      return {
        ...formatComparisonStat(vsGamesCompleted90DComparison, 'Completed VS Games', periodLbl)!,
        bars: getBars(),
      };
    }

    const currentCompleted = periodStats.periodData[periodStats.periodData.length - 1]?.vsGamesCompleted || 0;
    const previousCompleted = periodStats.periodData[periodStats.periodData.length - 2]?.vsGamesCompleted || 0;
    const completedChange = calculatePercentageChange(previousCompleted, currentCompleted);

    return {
      label: 'Completed VS Games',
      value: currentCompleted.toLocaleString(),
      change: `${completedChange >= 0 ? '+' : ''}${completedChange.toFixed(2)}% ${periodLbl}`,
      positive: completedChange >= 0,
      bars: getBars(),
    };
  }, [periods.completedVsGames, getPeriodStats, vsGamesCompleted7DComparison, vsGamesCompleted30DComparison, vsGamesCompleted90DComparison, vsGamesCompletedCustomComparison, customData]);

  const vsBotsStat = useMemo(() => {
    const period = periods.completedVsBots;
    const periodStats = getPeriodStats(period);
    const periodLbl = getPeriodLabel(period);

    const getBars = () => (period === 'custom' ? customData : periodStats.periodData).map((d) => d.vsBot || 0);

    if (period === 'custom' && vsBotCustomComparison) {
      return {
        ...formatComparisonStat(vsBotCustomComparison, 'VS Bots', periodLbl)!,
        bars: getBars(),
      };
    }

    if (period === '7D' && vsBot7DComparison) {
      return { ...formatComparisonStat(vsBot7DComparison, 'VS Bots', periodLbl)!, bars: getBars() };
    }
    if (period === '30D' && vsBot30DComparison) {
      return { ...formatComparisonStat(vsBot30DComparison, 'VS Bots', periodLbl)!, bars: getBars() };
    }
    if (period === '90D' && vsBot90DComparison) {
      return { ...formatComparisonStat(vsBot90DComparison, 'VS Bots', periodLbl)!, bars: getBars() };
    }

    const currentVsBots = periodStats.periodData[periodStats.periodData.length - 1]?.vsBot || 0;
    const previousVsBots = periodStats.periodData[periodStats.periodData.length - 2]?.vsBot || 0;
    const change = calculatePercentageChange(previousVsBots, currentVsBots);

    return {
      label: 'VS Bots',
      value: currentVsBots.toLocaleString(),
      change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}% ${periodLbl}`,
      positive: change >= 0,
      bars: getBars(),
    };
  }, [periods.completedVsBots, getPeriodStats, vsBot7DComparison, vsBot30DComparison, vsBot90DComparison, vsBotCustomComparison, customData]);

  // Export
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  const buildExportData = useCallback((): DashboardExportData => ({
    dailyActiveUsers: dailyActiveUsersStat,
    vsGames: vsGamesStat,
    singleGames: singleGamesStat,
    dailyChallengeMain: dailyChallengeMainStat,
    dailyChallengeStats: dailyChallengeStatsData,
    completedVsGames: completedVsGamesStat,
    vsBots: vsBotsStat,
    topPerformers,
    chartData,
    periods,
  }), [dailyActiveUsersStat, vsGamesStat, singleGamesStat, dailyChallengeMainStat, dailyChallengeStatsData, completedVsGamesStat, vsBotsStat, topPerformers, chartData, periods]);

  const handleExportPdf = useCallback(async () => {
    if (!dashboardRef.current) return;
    setExportDropdownOpen(false);
    setExporting(true);
    try {
      await exportDashboardPdf(dashboardRef.current, theme.bg);
    } finally {
      setExporting(false);
    }
  }, [theme.bg]);

  const handleExportCsv = useCallback(() => {
    setExportDropdownOpen(false);
    exportDashboardCsv(buildExportData());
  }, [buildExportData]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Styles
  const styles = {
    container: {
      padding: isMobile ? '12px' : '20px',
      paddingTop: isMobile ? '24px' : '36px',
      maxWidth: '1600px',
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap' as const,
      gap: '12px',
    },
    title: {
      fontSize: isMobile ? '24px' : '28px',
      fontWeight: 700,
      color: theme.text,
      margin: 0,
    },
    topStatsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : '1fr 1fr 1fr 1.4fr',
      gap: '12px',
      marginBottom: '16px',
    },
    middleSection: {
      display: 'grid',
      gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 320px',
      gap: '12px',
      marginBottom: '16px',
    },
    rightSidebar: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    bottomStatsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
      gap: '12px',
    },
    loadingCard: {
      background: theme.card,
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '120px',
    },
    errorCard: {
      background: theme.card,
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    spinner: {
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
    },
  };

  return (
    <div ref={dashboardRef} style={styles.container}>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Admin Dashboard</h1>
        <div data-export-hide style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' as const }}>
          <UniversalFilter onChangeAll={handleUniversalFilterChange} onCustomRangeAll={handleUniversalCustomRange} theme={theme} />
          <div ref={exportDropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setExportDropdownOpen(prev => !prev)}
              disabled={statsLoading || chartLoading || exporting}
              style={{
                padding: '8px 16px',
                background: statsLoading || chartLoading || exporting ? '#4b5563' : '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: statsLoading || chartLoading || exporting ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: statsLoading || chartLoading || exporting ? 0.6 : 1,
                whiteSpace: 'nowrap' as const,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {exporting ? 'Exporting...' : 'Export ▾'}
            </button>
            {exportDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 100,
                minWidth: '150px',
                overflow: 'hidden',
              }}>
                <button
                  onClick={handleExportPdf}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    color: theme.text,
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderBottom: `1px solid ${theme.border}`,
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = theme.tableRowHover; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="1" width="12" height="14" rx="1.5" stroke="#ef4444" strokeWidth="1.5" fill="none" />
                    <text x="8" y="11" textAnchor="middle" fill="#ef4444" fontSize="5.5" fontWeight="700" fontFamily="Arial">PDF</text>
                  </svg>
                  Export as PDF
                </button>
                <button
                  onClick={handleExportCsv}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'transparent',
                    border: 'none',
                    color: theme.text,
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = theme.tableRowHover; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="1" width="12" height="14" rx="1.5" stroke="#22c55e" strokeWidth="1.5" fill="none" />
                    <text x="8" y="11" textAnchor="middle" fill="#22c55e" fontSize="5.5" fontWeight="700" fontFamily="Arial">CSV</text>
                  </svg>
                  Export as CSV
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {statsError && (
        <div style={styles.errorCard}>
          <div>
            <div style={{ color: '#ef4444', fontWeight: 600, marginBottom: '4px' }}>Failed to load statistics</div>
            <div style={{ color: theme.textSecondary, fontSize: '14px' }}>{statsError}</div>
          </div>
          <button
            onClick={retry}
            style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Top Stats */}
      <div style={styles.topStatsGrid}>
        {statsLoading ? (
          [1, 2, 3, 4].map((i) => (
            <div key={i} style={styles.loadingCard}>
              <div style={styles.spinner} />
            </div>
          ))
        ) : (
          <>
            <StatCard {...dailyActiveUsersStat} theme={theme} isMobile={isMobile} period={periods.dailyActiveUsers} onPeriodChange={(p) => updatePeriod('dailyActiveUsers', p as PeriodOption)} customRange={periods.customRanges.dailyActiveUsers} onCustomRangeChange={(r) => handleCustomRangeChange('dailyActiveUsers', r)} onActionClick={handleViewActiveUsers} actionLabel="View Users" />
            <StatCard {...vsGamesStat} theme={theme} isMobile={isMobile} period={periods.vsGames} onPeriodChange={(p) => updatePeriod('vsGames', p as PeriodOption)} customRange={periods.customRanges.vsGames} onCustomRangeChange={(r) => handleCustomRangeChange('vsGames', r)} />
            <StatCard {...singleGamesStat} theme={theme} isMobile={isMobile} period={periods.singleGames} onPeriodChange={(p) => updatePeriod('singleGames', p as PeriodOption)} customRange={periods.customRanges.singleGames} onCustomRangeChange={(r) => handleCustomRangeChange('singleGames', r)} />
            <DailyChallengeCard
              periodTitle="Daily"
              mainValue={dailyChallengeMainStat.value}
              mainChange={dailyChallengeMainStat.change}
              mainChangePositive={dailyChallengeMainStat.changePositive}
              successRate={dailyChallengeMainStat.successRate}
              stats={dailyChallengeStatsData}
              theme={theme}
              isMobile={isMobile}
              period={periods.dailyChallenge}
              onPeriodChange={(p) => updatePeriod('dailyChallenge', p as PeriodOption)}
              customRange={periods.customRanges.dailyChallenge}
              onCustomRangeChange={(r) => handleCustomRangeChange('dailyChallenge', r)}
            />
          </>
        )}
      </div>

      {/* Chart + Right SideArea */}
      <div style={styles.middleSection}>
        <ChartCard
          chartData={chartData}
          chartPeriod={periods.chart}
          onPeriodChange={(p) => updatePeriod('chart', p)}
          totalGamesPlayed={totalGamesPlayed}
          activeLines={activeLines}
          onToggleLine={(key) => setActiveLines((prev) => ({ ...prev, [key]: !prev[key] }))}
          loading={chartLoading}
          theme={theme}
          isMobile={isMobile}
          customRange={periods.customRanges.chart}
          onCustomRangeChange={(r) => handleCustomRangeChange('chart', r)}
        />

        <div style={styles.rightSidebar}>
          <RevenueCard isMobile={isMobile} theme={theme} />
          <PerformersCard performers={topPerformers} loading={topPerformersLoading} period={periods.topPerformers} onPeriodChange={(p) => updatePeriod('topPerformers', p as PeriodOption)} onViewPerformer={(performer) => { const modeKey = getModeKey(performer.mode); setModalMode({ mode: performer.mode, modeKey }); fetchTopPerformers(modeKey, periods.topPerformers, topPerformersCustomRange); }} theme={theme} customRange={periods.customRanges.topPerformers} onCustomRangeChange={(r) => handleCustomRangeChange('topPerformers', r)} />
        </div>
      </div>

      {/* Bottom Stats */}
      <div style={styles.bottomStatsGrid}>
        <BottomStatCard {...completedVsGamesStat} theme={theme} period={periods.completedVsGames} onPeriodChange={(p) => updatePeriod('completedVsGames', p as PeriodOption)} customRange={periods.customRanges.completedVsGames} onCustomRangeChange={(r) => handleCustomRangeChange('completedVsGames', r)} />
        <BottomStatCard {...vsBotsStat} theme={theme} period={periods.completedVsBots} onPeriodChange={(p) => updatePeriod('completedVsBots', p as PeriodOption)} customRange={periods.customRanges.completedVsBots} onCustomRangeChange={(r) => handleCustomRangeChange('completedVsBots', r)} />
        <BottomStatCard label="Board Reward Ads" value="---" change="Coming soon" positive bars={[]} theme={theme} periodBadge="N/A" barColor="#6b7280" disabled />
        <BottomStatCard label="Hint Reward Ads" value="---" change="Coming soon" positive bars={[]} theme={theme} periodBadge="N/A" barColor="#6b7280" disabled />
        <BottomStatCard label="Interstitial Video Ads" value="---" change="Coming soon" positive bars={[]} theme={theme} periodBadge="N/A" barColor="#6b7280" disabled />
      </div>

      {modalMode && (
        <TopPerformersModal
          mode={modalMode.mode}
          performers={modalPerformers}
          loading={modalLoading}
          error={modalError}
          onClose={() => { setModalMode(null); resetModal(); }}
          onViewPlayer={(userId) => navigate(`/Admin/Users/Players/${userId}`)}
        />
      )}

      {showActiveUsersModal && (
        <ActiveUsersModal
          periodLabel={getPeriodLabel(periods.dailyActiveUsers).replace('from ', '')}
          users={activeUsers}
          loading={activeUsersLoading}
          error={activeUsersError}
          totalCount={activeUsersTotalCount}
          page={activeUsersPage}
          pageSize={activeUsersPageSize}
          onClose={() => { setShowActiveUsersModal(false); resetActiveUsers(); }}
          onPageChange={handleActiveUsersPageChange}
          onViewPlayer={(userId) => navigate(`/Admin/Users/Players/${userId}`)}
        />
      )}
    </div>
  );
};

export default Dashboard;
